import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Input } from "@/components/ui/input";
import { X, Search } from "lucide-react";
import { buildTree, flattenTree, type TreeItem } from "@/lib/tree-utils";

export interface PickableItem extends TreeItem {
  _key: string;
  name: string;
  icon: string;
  parent_key?: string | null;
}

interface RelatedItemPickerProps {
  /** All available items to pick from */
  items: PickableItem[];
  /** Currently selected related keys */
  selectedKeys: string[];
  /** Called when selection changes */
  onChange: (keys: string[]) => void;
  /** Key of the item being edited (excluded from list) */
  excludeKey?: string;
  /** Placeholder text */
  placeholder?: string;
}

export function RelatedItemPicker({
  items,
  selectedKeys,
  onChange,
  excludeKey,
  placeholder = "Search related items...",
}: RelatedItemPickerProps) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node) &&
          listRef.current && !listRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Track the position of the container for the portal dropdown
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      bottom: window.innerHeight - rect.top + 4,
      maxHeight: Math.min(rect.top - 8, window.innerHeight * 0.6),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updateDropdownPosition();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [open, updateDropdownPosition]);

  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  // Build tree-sorted list, then filter by search
  // When a parent matches, keep all its children visible too
  const filteredItems = useMemo(() => {
    const available = items.filter(i => i._key !== excludeKey);
    const tree = buildTree(available);
    const flat = flattenTree(tree, new Set());
    const sorted = flat.map(f => ({ ...f.item, _depth: f.depth }));
    if (!search.trim()) return sorted;
    const q = search.toLowerCase();
    // Collect keys of items that match directly
    const matchedKeys = new Set(sorted.filter(i => i.name.toLowerCase().includes(q)).map(i => i._key));
    // For each matched parent, also include all its descendants
    const includedKeys = new Set(matchedKeys);
    for (const item of sorted) {
      // Walk up the parent chain — if any ancestor matched, include this item
      let cur = item.parent_key;
      while (cur) {
        if (matchedKeys.has(cur)) { includedKeys.add(item._key); break; }
        const parent = available.find(i => i._key === cur);
        cur = parent?.parent_key ?? null;
      }
    }
    return sorted.filter(i => includedKeys.has(i._key));
  }, [items, excludeKey, search]);

  // Reset highlight when the filtered list changes
  useEffect(() => { setHighlightIdx(-1); }, [filteredItems]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    const count = filteredItems.length;
    if (count === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx(prev => (prev + 1) % count);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx(prev => (prev - 1 + count) % count);
    } else if (e.key === "Enter" && highlightIdx >= 0 && highlightIdx < count) {
      e.preventDefault();
      toggleItem(filteredItems[highlightIdx]!._key);
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIdx < 0 || !listRef.current) return;
    const el = listRef.current.children[highlightIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [highlightIdx]);

  const selectedItems = useMemo(
    () => items.filter(i => selectedSet.has(i._key)),
    [items, selectedSet],
  );

  const toggleItem = (key: string) => {
    if (selectedSet.has(key)) {
      onChange(selectedKeys.filter(k => k !== key));
    } else {
      onChange([...selectedKeys, key]);
    }
  };

  const removeItem = (key: string) => {
    onChange(selectedKeys.filter(k => k !== key));
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-text-muted" />
        <Input
          ref={inputRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="pl-8 h-9 text-sm"
        />
      </div>

      {/* Selected chips — icon + title only */}
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedItems.map(item => (
            <span
              key={item._key}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-app-accent/10 text-app-accent text-info font-medium"
            >
              <span>{item.icon}</span>
              <span className="truncate max-w-30">{item.name}</span>
              <button
                type="button"
                onClick={() => removeItem(item._key)}
                className="ml-0.5 hover:text-app-danger transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown with hierarchy — portaled to escape overflow containers */}
      {open && createPortal(
        <div ref={listRef} style={dropdownStyle} className="z-70 overflow-y-auto rounded-lg border border-app-border bg-app-surface shadow-lg">
          {filteredItems.length === 0 ? (
            <p className="p-3 text-info text-app-text-muted text-center">No items found</p>
          ) : (
            filteredItems.map((item, idx) => {
              const depth = item._depth;
              const isSelected = selectedSet.has(item._key);
              const isHighlighted = idx === highlightIdx;
              return (
                <button
                  key={item._key}
                  type="button"
                  onClick={() => toggleItem(item._key)}
                  onMouseEnter={() => setHighlightIdx(idx)}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 transition-colors text-sm ${
                    isSelected ? "bg-app-accent/5 text-app-accent font-medium" : "text-app-text"
                  } ${isHighlighted ? "bg-app-accent/10" : "hover:bg-app-accent/10"}`}
                >
                  {depth > 0 && (
                    <span className="text-app-text-muted shrink-0">{"─".repeat(depth)}</span>
                  )}
                  <span className="shrink-0">{item.icon}</span>
                  <span className="truncate">{item.name}</span>
                  {isSelected && (
                    <span className="ml-auto text-app-accent shrink-0">✓</span>
                  )}
                </button>
              );
            })
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
